"use client"

import React, { useEffect, useMemo, useState } from "react"
import styles from "./HolidaySnow.module.css"

const HOLIDAY_SNOW_START_UTC_MS = Date.parse("2025-12-21T00:00:00Z")
const HOLIDAY_SNOW_END_UTC_MS = Date.parse("2026-01-05T23:59:59Z")

type Snowflake = {
  id: string
  leftPercent: number
  sizePx: number
  durationS: number
  delayS: number
  opacity: number
  blurPx: number
  driftPx: number
}

function isWithinHolidaySnowWindowUtcMs(nowUtcMs: number): boolean {
  return nowUtcMs >= HOLIDAY_SNOW_START_UTC_MS && nowUtcMs <= HOLIDAY_SNOW_END_UTC_MS
}

function createSnowflakes(count: number): Snowflake[] {
  const clamped = Math.max(0, Math.min(count, 120))

  return Array.from({ length: clamped }, (_, i) => {
    // Distribute left a bit more evenly than pure random to avoid clusters
    const base = (i / Math.max(1, clamped - 1)) * 100
    const jitter = (Math.random() - 0.5) * 10
    const leftPercent = Math.max(0, Math.min(100, base + jitter))

    // Make flakes a bit more visible on white background (~1.2x size)
    const sizePx = (1.5 + Math.random() * 3.5) * 1.2 // ~1.8–6px
    const durationS = 8 + Math.random() * 10 // 8–18s
    const delayS = -Math.random() * durationS // negative to "pre-seed" animation
    const opacity = 0.35 + Math.random() * 0.45 // 0.35–0.8
    const blurPx = Math.random() * 1.2 // 0–1.2px
    const driftPx = 10 + Math.random() * 90 // 10–100px sideways drift

    return {
      id: `flake-${i}-${Math.random().toString(16).slice(2)}`,
      leftPercent,
      sizePx,
      durationS,
      delayS,
      opacity,
      blurPx,
      driftPx,
    }
  })
}

function getPrefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false
  if (typeof window.matchMedia !== "function") return false
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

export function HolidaySnow({
  flakeCount = 50,
}: {
  flakeCount?: number
}) {
  const [enabled, setEnabled] = useState(false)
  const [flakes, setFlakes] = useState<Snowflake[]>([])

  const resolvedFlakeCount = useMemo(() => {
    // Slightly fewer elements for small screens to keep things smooth.
    if (typeof window !== "undefined" && window.innerWidth <= 420) return Math.min(flakeCount, 32)
    return flakeCount
  }, [flakeCount])

  useEffect(() => {
    let mql: MediaQueryList | null = null

    const evaluate = () => {
      const now = Date.now()
      const inWindow = isWithinHolidaySnowWindowUtcMs(now)
      const reducedMotion = getPrefersReducedMotion()

      const nextEnabled = inWindow && !reducedMotion
      setEnabled(nextEnabled)
      setFlakes((prev) => {
        if (!nextEnabled) return []
        return prev.length > 0 ? prev : createSnowflakes(resolvedFlakeCount)
      })
    }

    evaluate()

    if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
      mql = window.matchMedia("(prefers-reduced-motion: reduce)")
      const onChange = () => evaluate()

      // Safari fallback supports addListener/removeListener
      if (typeof mql.addEventListener === "function") {
        mql.addEventListener("change", onChange)
      } else if (typeof mql.addListener === "function") {
        mql.addListener(onChange)
      }

      document.addEventListener("visibilitychange", evaluate)

      return () => {
        document.removeEventListener("visibilitychange", evaluate)
        if (!mql) return
        if (typeof mql.removeEventListener === "function") {
          mql.removeEventListener("change", onChange)
        } else if (typeof mql.removeListener === "function") {
          mql.removeListener(onChange)
        }
      }
    }

    document.addEventListener("visibilitychange", evaluate)
    return () => {
      document.removeEventListener("visibilitychange", evaluate)
    }
  }, [resolvedFlakeCount])

  if (!enabled) return null

  return (
    <div className={styles.overlay} aria-hidden="true">
      {flakes.map((flake) => {
        const styleVars = {
          ["--left" as any]: `${flake.leftPercent}%`,
          ["--size" as any]: `${flake.sizePx}px`,
          ["--duration" as any]: `${flake.durationS}s`,
          ["--delay" as any]: `${flake.delayS}s`,
          ["--opacity" as any]: `${flake.opacity}`,
          ["--blur" as any]: `${flake.blurPx}px`,
          ["--drift" as any]: `${flake.driftPx}px`,
        } as React.CSSProperties

        return <span key={flake.id} className={styles.flake} style={styleVars} />
      })}
    </div>
  )
}

